import { fetchApi } from "@/services/api"
import { mockSmithersDeviceService } from "@/mocks/smithers-device-service"
import { validateSmithersBulkDeviceIds } from "@/lib/smithers-bulk"
import { isSmithersBulkUpdateResult, validateSmithersBulkUpdateRequest } from "@/lib/smithers-bulk-update"
import { ApiException } from "@/types/api"
import type { SmithersBulkImportJobApiResponse, SmithersBulkImportJobStatusResponse, SmithersBulkImportUploadResult } from "@/types/smithers-bulk-import"
import type { BulkUpdateSmithersDevicesRequest, BulkUpdateSmithersDevicesResult } from "@/types/smithers-device"
import type { BulkDeleteSmithersDevicesResult, CreateSmithersDeviceRequest, UpdateSmithersDeviceRequest, SmithersDeviceConfig, SmithersDeviceListParams, SmithersDevicePage } from "@/types/smithers-device"

function listQuery(params: SmithersDeviceListParams): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") query.set(key, String(value))
  }
  return query.size ? `?${query}` : ""
}

const realSmithersDeviceService = {
  async bulkCreate(file: File): Promise<SmithersBulkImportUploadResult> {
    const body = new FormData()
    body.append("file", file)
    try {
      return await fetchApi<SmithersBulkImportUploadResult>("/api/v1/config/smithers/devices/bulk/create", { method: "POST", body })
    } catch (error) {
      if (error instanceof ApiException && typeof error.payload === "object" && error.payload !== null && "details" in error.payload) {
        const details = error.payload.details as { rejections?: { row: number; error: string }[] } | undefined
        if (Array.isArray(details?.rejections)) error.message += ` ${details.rejections.map((item) => `Row ${item.row}: ${item.error}`).join("; ")}`
      }
      throw error
    }
  },
  async getBulkImportJob(jobId: string): Promise<SmithersBulkImportJobStatusResponse> {
    const response = await fetchApi<SmithersBulkImportJobApiResponse>(`/api/v1/config/smithers/devices/bulk/${encodeURIComponent(jobId)}`)
    const { results, ...job } = response
    return { ...job, skippedDuplicate: 0, errorSample: results?.map(({ row, ...item }) => ({ ...item, rowNumber: row })) ?? [] }
  },
  async getAll(params: SmithersDeviceListParams = {}): Promise<SmithersDevicePage> {
    return fetchApi<SmithersDevicePage>(`/api/v1/config/smithers/devices${listQuery(params)}`)
  },
  async getByClient(id: number, params: SmithersDeviceListParams = {}): Promise<SmithersDevicePage> {
    return fetchApi<SmithersDevicePage>(`/api/v1/config/smithers/clients/${id}/devices${listQuery(params)}`)
  },
  async create(data: CreateSmithersDeviceRequest): Promise<SmithersDeviceConfig> {
    return fetchApi<SmithersDeviceConfig>("/api/v1/config/smithers/devices", { method: "POST", body: JSON.stringify(data) })
  },
  async getById(id: number): Promise<SmithersDeviceConfig> {
    return fetchApi<SmithersDeviceConfig>(`/api/v1/config/smithers/devices/${id}`)
  },
  async update(id: number, data: UpdateSmithersDeviceRequest): Promise<SmithersDeviceConfig> {
    return fetchApi<SmithersDeviceConfig>(`/api/v1/config/smithers/devices/${id}`, { method: "PUT", body: JSON.stringify(data) })
  },
  async delete(id: number): Promise<void> {
    return fetchApi<void>(`/api/v1/config/smithers/devices/${id}`, { method: "DELETE" })
  },
  async bulkDelete(ids: number[]): Promise<BulkDeleteSmithersDevicesResult> {
    validateSmithersBulkDeviceIds(ids)
    const result = await fetchApi<BulkDeleteSmithersDevicesResult | null>("/api/v1/config/smithers/devices/bulk/delete", { method: "POST", body: JSON.stringify({ ids }) })
    return result ?? { results: ids.map((id) => ({ id, status: "deleted" as const })), deleted: ids.length, failed: 0 }
  },
  async bulkUpdate(data: BulkUpdateSmithersDevicesRequest): Promise<BulkUpdateSmithersDevicesResult> {
    validateSmithersBulkUpdateRequest(data)
    try {
      return await fetchApi<BulkUpdateSmithersDevicesResult>("/api/v1/config/smithers/devices/bulk/update", { method: "PUT", body: JSON.stringify(data) })
    } catch (error) {
      // All-device failures use 400/500 while retaining the per-device result body.
      if (error instanceof ApiException && isSmithersBulkUpdateResult(error.payload)) return error.payload
      throw error
    }
  },
}

export const smithersDeviceService = import.meta.env.VITE_USE_MOCKS === "true"
  ? mockSmithersDeviceService
  : realSmithersDeviceService
